import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { get, checkIfPrepared } from "../../../client/api/session";
import { FetchProvider, useFetch } from "../../../core/components/crud/contexts/fetch.context";
import { Box, Button, Typography, Paper, Grid, Card, CardContent, Container, Divider, Chip } from "@mui/material";
import { SessionFetchParameters } from "common/build/api-parameters/session";
import { SessionType } from "../../../models/modelTypes";
import Spinner from "../../layout/Spinner";
import { WarningAmberRounded, PlayCircleOutline, ImportContacts } from "@mui/icons-material";
import { Breadcrumb } from "../../../core/components/Breadcrumb";
import { useMobile } from "../../../hooks/useMobile";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { SessionMoodPanel } from "../../assess/mood/SessionMoodPanel";

const params: SessionFetchParameters = {
    includeStory: true,
};

const useStyles = makeStyles(() =>
    createStyles({
        prepareLayout: {
            display: "grid",
            gridTemplateColumns: "auto",
        },
    }),
);

const DeliverSessionOverviewContent: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { item: session, fetchItem } = useFetch<SessionType>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isMobile, isTablet } = useMobile();
    const classes = useStyles();
    const [hasPrepared, setHasPrepared] = useState<boolean | null>(null);

    const classroomId = location.state?.classroomId;

    useEffect(() => {
        if (id) {
            fetchItem(parseInt(id));
        }
    }, [id]);

    useEffect(() => {
        if (session?.id) {
            checkIfPrepared(session.id).then(setHasPrepared);
        }
    }, [session?.id]);

    const programmeUrl = useMemo(() => {
        return classroomId
            ? `/classrooms/view/${classroomId}/view/${session?.programmeId}`
            : `/programmes/view/${session?.programmeId}`;
    }, [classroomId, session?.programmeId]);

    if (!session || hasPrepared === null) {
        return <Spinner />;
    }

    const programmeName = session.Programme?.name || "Unknown Programme";
    const sessionType = session.Programme?.isGroup ? "Group Session" : "1-to-1 Session";
    const storyName = session.MappedStory?.Story?.storyName || "Unknown Story";
    const story = session.MappedStory?.Story;
    const overview = story?.overview?.trim() || story?.synopsis?.trim() || "No overview available for this story.";

    const dateFormatted = new Date(session.date).toLocaleString(undefined, {
        weekday: "short",
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });

    return (
        <div className={classes.prepareLayout}>
            {!isTablet && isMobile && <div style={{ marginTop: 56 }} />}
            <Box p={!isTablet && isMobile ? 2 : 4} width={1}>
                <Container maxWidth={"xl"} style={isMobile ? { paddingLeft: 0, paddingRight: 0 } : undefined}>
                    <Box mt={4} mb={2} pl={6}>
                        <Breadcrumb crumbs={[{ name: "Programme", url: programmeUrl }]} current={"Deliver Session"} />
                    </Box>

                    <Box mb={2} pl={6}>
                        <Typography variant="h1">{storyName}</Typography>
                    </Box>

                    <Box pl={6} pt={2} maxWidth="1000px">
                        {!hasPrepared && (
                            <Card
                                sx={{ mb: 4, borderLeft: "6px solid", borderColor: "warning.main", bgcolor: "#fff4e5" }}
                                elevation={2}
                            >
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: 3,
                                    }}
                                >
                                    <WarningAmberRounded color="warning" sx={{ fontSize: 40 }} />
                                    <Box flex={1}>
                                        <Typography variant="h6" color="warning.dark" gutterBottom>
                                            You haven&apos;t prepared this session yet!
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            It is highly recommended to prepare each session before delivering it to a
                                            live group. There is important information and structure in the prepare page
                                            that isn&apos;t shown during delivery.
                                        </Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="column" gap={1} minWidth="200px">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            component={RouterLink}
                                            to={`/prepare-session/${session.id}`}
                                            startIcon={<ImportContacts />}
                                            fullWidth
                                        >
                                            Prepare Now
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                        <Paper
                            elevation={0}
                            sx={{ p: 4, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}
                        >
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={3}>
                                    <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        textTransform="uppercase"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        Programme
                                    </Typography>
                                    <Typography variant="h5" color="textPrimary">
                                        {programmeName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        textTransform="uppercase"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        Type
                                    </Typography>
                                    <Typography variant="h5" color="textPrimary">
                                        {sessionType}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        textTransform="uppercase"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        Date & Time
                                    </Typography>
                                    <Typography variant="h5" color="textPrimary">
                                        {dateFormatted}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        textTransform="uppercase"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        Status
                                    </Typography>
                                    <Box mt={0.5}>
                                        <Chip
                                            label={hasPrepared ? "Prepared" : "Unprepared"}
                                            color={hasPrepared ? "success" : "warning"}
                                            variant="outlined"
                                            size="medium"
                                            sx={{ fontWeight: "bold", fontSize: "0.9rem" }}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        textTransform="uppercase"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        Story Overview
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}
                                    >
                                        {overview}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* VER-118: mood check-in, sessions 2–5 only. The panel returns null
                            outside that window, for users without SurveyRespond, and for school
                            accounts — School Admin is FullAccess but this capture is not shipped
                            to schools yet. */}
                        {session.programmeId ? (
                            <SessionMoodPanel
                                sessionId={session.id}
                                programmeId={session.programmeId}
                                sessionNumber={session.sessionNumber}
                            />
                        ) : null}

                        <Box display="flex" justifyContent="flex-start" mt={4}>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="large"
                                onClick={() => navigate(`/start-session/${session.id}`)}
                                startIcon={<PlayCircleOutline sx={{ fontSize: 28 }} />}
                                sx={{ px: 6, py: 1.5, fontSize: "1.2rem", borderRadius: 8, fontWeight: "bold" }}
                            >
                                Start Session
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </div>
    );
};

export const DeliverSessionOverview: React.FC = () => {
    return (
        <FetchProvider<SessionType> noun={"session"} getItem={get} includes={params}>
            <DeliverSessionOverviewContent />
        </FetchProvider>
    );
};
